import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field, FileInput } from 'components';
import {
  FileList,
  FileRow,
  PendingFile,
  EmptyNote,
  LinkButton,
  UploadRow,
} from '../Reports.styled';

/** "2 MB", "512 KB" - the same wording the API answers with. */
const formatSize = (bytes) =>
  bytes >= 1048576
    ? `${Number((bytes / 1048576).toFixed(1))} MB`
    : `${Math.round(bytes / 1024)} KB`;

/**
 * One group of documents on the report: a file input that keeps taking files,
 * with everything picked so far listed under it.
 *
 * Documents that are already stored are links, so they can be read back before
 * the report is sent in again; the ones picked in this session are plain names
 * until the save that uploads them goes through.
 *
 * A file that is too big or of the wrong format is refused here rather than on
 * save: the server refuses it either way, and finding that out on the spot
 * beats losing a round trip and the rest of the form's scroll position.
 */
const DocumentGroup = ({
  id,
  label,
  description,
  required,
  disabled,
  error,
  extensions,
  maxSize,
  stored,
  pending,
  onAdd,
  onRemoveStored,
  onRemovePending,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState(null);

  const accept = extensions.map((extension) => `.${extension}`).join(',');

  const refuse = (file) => {
    const extension = (file.name.split('.').pop() || '').toLowerCase();

    if (extensions.length && !extensions.includes(extension)) {
      return t('page.yc.reports.form.fileWrongFormat', {
        name: file.name,
        formats: extensions.join(', '),
      });
    }

    if (maxSize && file.size > maxSize) {
      return t('page.yc.reports.form.fileTooLarge', {
        name: file.name,
        size: formatSize(maxSize),
      });
    }

    return null;
  };

  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    const refused = files.map(refuse).find(Boolean);

    files.filter((file) => !refuse(file)).forEach(onAdd);
    setLocalError(refused || null);

    // The same file has to be pickable again after it was dropped, and the
    // input only fires on a change - so it is emptied after every pick.
    if (inputRef.current) inputRef.current.value = '';
  };

  const hasFiles = !!stored.length || !!pending.length;

  return (
    <Field
      id={id}
      label={label}
      description={description}
      required={required}
      error={error || localError}
    >
      <div>
        {!disabled ? (
          <UploadRow>
            <FileInput
              id={id}
              ref={inputRef}
              multiple
              accept={accept}
              onChange={handleChange}
              clear={() => {}}
              placeholder={t('page.yc.reports.form.filePlaceholder')}
            />
          </UploadRow>
        ) : null}

        {hasFiles ? (
          <FileList>
            {stored.map((file) => (
              <FileRow key={`stored-${file.id}`}>
                <a href={file.url} target="_blank" rel="noopener noreferrer">
                  {file.name}
                </a>
                {!disabled ? (
                  <LinkButton type="button" onClick={() => onRemoveStored(file.id)}>
                    {t('global.removeButton')}
                  </LinkButton>
                ) : null}
              </FileRow>
            ))}
            {pending.map((file, index) => (
              // A picked file has nothing unique about it but its place in the
              // list, and that place is what the remove button addresses.
              // eslint-disable-next-line react/no-array-index-key
              <FileRow appear key={`pending-${index}-${file.name}`}>
                <PendingFile>
                  {t('page.yc.reports.form.pendingFile', { name: file.name })}
                </PendingFile>
                <LinkButton type="button" onClick={() => onRemovePending(index)}>
                  {t('global.removeButton')}
                </LinkButton>
              </FileRow>
            ))}
          </FileList>
        ) : (
          <EmptyNote>
            {disabled
              ? t('page.yc.reports.form.noFilesRead')
              : t('page.yc.reports.form.noFiles', {
                  formats: extensions.join(', '),
                  size: maxSize ? formatSize(maxSize) : '',
                })}
          </EmptyNote>
        )}
      </div>
    </Field>
  );
};

DocumentGroup.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  extensions: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
  stored: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  pending: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  onAdd: PropTypes.func,
  onRemoveStored: PropTypes.func,
  onRemovePending: PropTypes.func,
};

DocumentGroup.defaultProps = {
  description: undefined,
  required: false,
  disabled: false,
  error: undefined,
  extensions: [],
  maxSize: null,
  stored: [],
  pending: [],
  onAdd: () => undefined,
  onRemoveStored: () => undefined,
  onRemovePending: () => undefined,
};

export default DocumentGroup;
